import React, { useEffect } from 'react'
import Button from '@material-ui/core/Button'
import FormikTextField from 'components/Fields/FormikTextField'
import { FieldArray, Form, Formik, FormikHelpers, useFormikContext } from 'formik'
import * as yup from 'yup'
import Alert from '@material-ui/lab/Alert'
import { extractError, isFieldError } from 'utils/api'
import { useIntl } from 'react-intl'
import IntlMessages from '@crema/utility/IntlMessages'
import Box from '@material-ui/core/Box'
import { IApiState } from 'store/types'
import { apiStatus, BlockPeriods, OfferingTypes } from 'shared/constants/AppEnum'
import { IOfferingFormType } from 'types/store/temple'
import MenuItem from '@material-ui/core/MenuItem'
import { capStr } from 'utils/fns'
import Divider from '@material-ui/core/Divider'
import Grid from '@material-ui/core/Grid'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import { CremaTheme } from 'types/crema/AppContextPropsType'
import WeeklyDays from '../Bookable/WeeklyDays'
import MonthDays from '../Bookable/MonthDays'
import OfferingTimes from './OfferingTimes'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { offeringCategoryListThunk, selectCurrentTempleOfferingCategories } from 'store/reducers/temple/offeringCategorySlice'
import { IOfferingResponse, ITempleResponse } from 'types/api/temple'
import RadioGroup from '@material-ui/core/RadioGroup'
import FormControlLabel from '@material-ui/core/FormControlLabel'
import Radio from '@material-ui/core/Radio'
import FormControl from '@material-ui/core/FormControl'
import FormLabel from '@material-ui/core/FormLabel'
import FormGroup from '@material-ui/core/FormGroup'
import Checkbox from '@material-ui/core/Checkbox'
import FullDates from '../Bookable/FullDates'
import FormikSwitchField from 'components/SwitchField/SwitchField'
import { getOfferingFormInitValues } from 'utils/temple/offering'
import PriceSplitForm from './PriceSplitForm'
import { FeeSplitNameResponseType } from 'api/report_app/report_app.response'
import { FormHelperText } from '@material-ui/core'
import MultiDeitySelect from './MultiDeitySelect'
import { deityListThunk, selectCurrentTempleDeities } from 'store/reducers/temple/deitySlice'
import PoolSelect from './PoolSelect'

const useStyles = makeStyles((theme: CremaTheme) =>
    createStyles({
        formLeftPane: {
            // marginBottom: "1rem",
            [theme.breakpoints.up('sm')]: {
                marginRight: "2rem"
            },
        },
        formRightPane: {
            marginTop: "1rem",
        }
    }),
);

type FormSubmitHandleType =
    ((values: IOfferingFormType, formikHelpers: FormikHelpers<IOfferingFormType>) => void | Promise<any>) & Function

const BlockUnitCleaner: React.FC = () => {
    const { values, setFieldValue } = useFormikContext<IOfferingFormType>()

    useEffect(() => {
        if (values.blockPeriod === BlockPeriods.NA && values.blockUnit !== '') {
            setFieldValue('blockUnit', '')
        }
    }, [setFieldValue, values.blockPeriod, values.blockUnit])

    return null
}

interface FormProps {
    buttonLabelId: string,
    initialValues?: IOfferingFormType | null,
    onSubmitCb: FormSubmitHandleType
    api: IApiState
    alerts: TqAlert[],
    offering?: IOfferingResponse,
    feeSplitNames: FeeSplitNameResponseType[],
    temple?: ITempleResponse | null,
    isEdit?: boolean
}
const FormOffering: React.FC<FormProps> = (props) => {
    const {
        buttonLabelId,
        initialValues,
        onSubmitCb,
        api,
        alerts,
        offering,
        feeSplitNames,
        temple,
        isEdit
    } = props
    const offeringCategories = useAppSelector(selectCurrentTempleOfferingCategories)
    const deities = useAppSelector(selectCurrentTempleDeities)
    const dispatch = useAppDispatch()
    const { messages } = useIntl();
    const classes = useStyles();

    useEffect(() => {
        if (offeringCategories.length === 0) {
            dispatch(offeringCategoryListThunk({}))
        }
        if (deities.length === 0) {
            dispatch(deityListThunk({}))
        }
    }, [dispatch, deities.length, offeringCategories.length])

    const validationSchema = yup.object({
        name: yup
            .string()
            .required('Name is required'),
        offeringType: yup
            .string()
            .oneOf(Object.values(OfferingTypes), 'Invalid Offering Type')
            .required('Type of offering is required'),
        category: yup
            .string()
            .required('Category is required'),
        price: yup
            .number()
            .min(
                temple?.allowZeroAmountTransactions ? 0 : 0.01,
                temple?.allowZeroAmountTransactions
                    ? 'Price cannot be negative'
                    : 'Price must be greater than zero'
            )
            .required('Price is required'),
        canChangePrice: yup.boolean().required(),
        assignedDeities: yup.array().of(yup.string()),
        poolConnections: yup.array().of(yup.string()),
        description: yup.string(),
        isRecommended: yup.boolean(),
        isCourierAvailable: yup.boolean(),
        allowMultipleDevotees: yup.boolean().required(),
        perDayLimit: yup.number().positive(),
        blockPeriod: yup
            .string()
            .oneOf(Object.values(BlockPeriods), 'Invalid block period'),
        blockUnit: yup.number().positive(),

        isDakshinaRequired: yup.boolean().required(),
        minDakshinaAmount: yup.number(),
        repeatUnit: yup.number().positive(),
        useClosingTime: yup.boolean(),
        offeringTimes: yup.object({
            fieldFromTime: yup.date(),
            fieldToTime: yup.date(),
            blockCalcChooser: yup
                .string()
                .oneOf(['from', 'to'], 'Ivalid offering time setting'),
        }),
        offeringMeta: yup.object({
            customDates: yup.object({
                fieldDate: yup.date(),
            }),
            excludedDates: yup.object({
                fieldDate: yup.date(),
            }),
        }),
        splitEntries: yup.array().of(yup.object({
            name: yup.string().required('required'),
            value: yup.number().required('required'),
            percent: yup.number().min(0).max(100).required('required')
        })).test({
            name: 'is-sum-100',
            message: 'Sum of percentages should be 100',
            test(arr, ctx) {
                if (arr === undefined || arr.length === 0) return true
                const total = arr?.reduce((acc, curr) => acc + (curr.percent ?? 0), 0)
                return total === 100
            }
        }).optional(),
        availableCounter: yup
            .boolean()
            .required()
            .test(
                'online-or-medium',
                "Select at least one: App/Web, Counter, Kiosk, or POS",
                (availableCounter, context) => {
                    const { availableOnline, availableKiosk, availablePos } = context.parent
                    return availableCounter || availableOnline || availableKiosk || availablePos
                }),
        availableOnline: yup
            .boolean()
            .required()
            .test(
                'online-or-medium',
                "Select at least one: App/Web, Counter, Kiosk, or POS",
                (availableOnline, context) => {
                    const { availableCounter, availableKiosk, availablePos } = context.parent
                    return availableOnline || availableCounter || availableKiosk || availablePos
                })
        ,
        availableKiosk: yup
            .boolean()
            .required()
            .test(
                'online-or-medium',
                "Select at least one: App/Web, Counter, Kiosk, or POS",
                (availableKiosk, context) => {
                    const { availableOnline, availableCounter, availablePos } = context.parent
                    return availableKiosk || availableOnline || availableCounter || availablePos
                }),
        availablePos: yup
            .boolean()
            .required()
            .test(
                'online-or-medium',
                "Select at least one: App/Web, Counter, Kiosk, or POS",
                (availablePos, context) => {
                    const { availableOnline, availableCounter, availableKiosk } = context.parent
                    return availablePos || availableOnline || availableCounter || availableKiosk
                }),
        tokenDayWise: yup.boolean().required(),
    })

    return (
        <Formik
            enableReinitialize
            validateOnChange={true}
            initialValues={initialValues && offeringCategories.length > 0 ?
                initialValues : getOfferingFormInitValues(temple?.closingTime !== undefined && temple?.closingTime !== null)
            }
            validationSchema={validationSchema}
            onSubmit={onSubmitCb}
        >
            {({ isSubmitting, dirty, values, isValid, setFieldValue, errors }) => {
                return (
                    <Form noValidate autoComplete='off'>
                        <BlockUnitCleaner />
                        <Grid container spacing={10}>
                            <Grid xs={12} item>
                                <Box className={classes.formLeftPane}>
                                    <FormikTextField
                                        placeholder={messages['common.name']}
                                        name='name'
                                        label={<IntlMessages id='common.name' />}
                                        variant='outlined'
                                        required
                                        fullWidth
                                    />

                                    <FormikTextField
                                        placeholder={"Temple Ref No"}
                                        name='templeRefNo'
                                        label={"Temple Ref No"}
                                        variant='outlined'
                                        required
                                        fullWidth
                                    />
                                    <FormikTextField
                                        select
                                        name='category'
                                        label={<IntlMessages id='offeringCategory' />}
                                        variant='outlined'
                                        required
                                        fullWidth
                                    >
                                        {offeringCategories.map(item => (
                                            <MenuItem key={item.id} value={item.url}>
                                                {item.name}
                                            </MenuItem>
                                        ))}
                                    </FormikTextField>

                                    <FormGroup>
                                        {/* <FormControlLabel
                                        name='isRecommended'
                                        control={
                                            <Checkbox
                                                size='small'
                                                onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                                                    setFieldValue(event.currentTarget.name, checked)
                                                }}
                                            />
                                        }
                                        checked={Boolean(values.isRecommended)}
                                        label={<IntlMessages id='offering.recommended' />}
                                    /> */}
                                        <FormControlLabel
                                            name='isCourierAvailable'
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                                                        setFieldValue(event.currentTarget.name, checked)
                                                    }}
                                                />
                                            }
                                            checked={Boolean(values.isCourierAvailable)}
                                            label={<IntlMessages id='offering.courierAvailable' />}
                                        />
                                        <FormControlLabel
                                            name='availableOnline'
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                                                        setFieldValue(event.currentTarget.name, checked)
                                                        if (checked) setFieldValue('canChangePrice', false)
                                                    }}
                                                />
                                            }
                                            checked={Boolean(values.availableOnline)}
                                            label="Available App/Web"
                                        />
                                        <FormControlLabel
                                            name='availableCounter'
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                                                        setFieldValue(event.currentTarget.name, checked)
                                                        if (!checked) setFieldValue('canChangePrice', false)
                                                    }}
                                                />
                                            }
                                            checked={Boolean(values.availableCounter)}
                                            label="Available Counter"
                                        />
                                        <FormControlLabel
                                            name='availableKiosk'
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                                                        setFieldValue(event.currentTarget.name, checked)
                                                        if (!checked) setFieldValue('canChangePrice', false)
                                                    }}
                                                />
                                            }
                                            checked={Boolean(values.availableKiosk)}
                                            label="Available Kiosk"
                                        />
                                        <FormControlLabel
                                            name='availablePos'
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                                                        setFieldValue(event.currentTarget.name, checked)
                                                        if (!checked) setFieldValue('canChangePrice', false)
                                                    }}
                                                />
                                            }
                                            checked={Boolean(values.availablePos)}
                                            label="Available POS"
                                        />
                                        <FormControlLabel
                                            name='tokenEnabled'
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                                                        setFieldValue(event.currentTarget.name, checked)
                                                    }}
                                                />
                                            }
                                            checked={Boolean(values.tokenEnabled)}
                                            label="Enable Token"
                                        />
                                        <FormControlLabel
                                            name='tokenDayWise'
                                            control={
                                                <Checkbox
                                                    size='small'
                                                    onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                                                        setFieldValue(event.currentTarget.name, checked)
                                                    }}
                                                />
                                            }
                                            checked={values.tokenDayWise}
                                            label="Token Day Wise"
                                        />
                                        {(errors.availableCounter || errors.availableOnline || errors.availableKiosk || errors.availablePos) && (
                                            <FormHelperText error>{String(errors.availableCounter || errors.availableOnline || errors.availableKiosk || errors.availablePos)}</FormHelperText>
                                        )}

                                    </FormGroup>
                                    <Grid container spacing={10} alignItems='center'>
                                        <Grid item xs={12} md={6}>
                                            <FormikTextField
                                                placeholder={messages['common.price']}
                                                name='price'
                                                label={values?.canChangePrice ? "Default Price" : "Price"}
                                                variant='outlined'
                                                // value={values.price}
                                                type='number'
                                                required
                                                fullWidth
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <FormGroup>
                                                <FormControlLabel
                                                    name='canChangePrice'
                                                    disabled={!(!values.availableOnline && (values.availableCounter || values.availableKiosk))}
                                                    control={
                                                        <Checkbox
                                                            size='small'
                                                            onChange={(event: React.ChangeEvent<HTMLInputElement>, checked) => {
                                                                setFieldValue(event.currentTarget.name, checked)
                                                            }}
                                                        />
                                                    }
                                                    checked={Boolean(values.canChangePrice)}
                                                    label={"Allow Changing price"}
                                                />
                                            </FormGroup>

                                        </Grid>
                                    </Grid>

                                    {temple && <FieldArray name='splitEntries'>
                                        {(arrHelpers) => (
                                            <PriceSplitForm
                                                temple={temple}
                                                price={values.price !== "" ? values.price : undefined}
                                                feeSplitNames={feeSplitNames}
                                                // splitEntries={values.splitEntries}
                                                addNewEntry={() => arrHelpers.push({ name: '', percent: '', value: '' })}
                                                removeEntry={(index) => arrHelpers.remove(index)}
                                            />)
                                        }
                                    </FieldArray>}
                                    <FormikTextField
                                        select
                                        name='offeringType'
                                        label={<IntlMessages id='offering.offeringType' />}
                                        variant='outlined'
                                        required
                                        fullWidth
                                        style={{ paddingBottom: "20px" }}
                                    >
                                        {Object.entries(OfferingTypes).filter(([key, value]) => (value !== OfferingTypes.MONTHLY && value !== OfferingTypes.YEARLY)).map(([key, value]) => (
                                            <MenuItem key={key} value={value}>
                                                {capStr(value)}
                                            </MenuItem>
                                        ))}
                                    </FormikTextField>
                                    <MultiDeitySelect deities={values.assignedDeities} setDeities={(deities) => { setFieldValue('assignedDeities', deities) }} />
                                    {values.offeringTimes.times.length === 0 && values.poolConnections.length < 2 && (
                                        <FormikTextField
                                            placeholder={messages['offering.perDayLimit']}
                                            name='perDayLimit'
                                            label={<IntlMessages id='offering.perDayLimit' />}
                                            variant='outlined'
                                            fullWidth
                                        />)}
                                    <Box mt={4}>
                                        {values.offeringType === OfferingTypes.MONTHLY && (
                                            <MonthDays
                                                // selectedDays={monthDayState}
                                                // handleChange={handleOfferingMonthDay}
                                                formContext="offeringMeta.monthDays"
                                                formState={values.offeringMeta.monthDays}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.offeringType === OfferingTypes.WEEKLY && (
                                            <WeeklyDays
                                                formContext="offeringMeta.weekDays"
                                                formState={values.offeringMeta.weekDays}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                        {values.offeringType === OfferingTypes.CUSTOM ? (
                                            <FullDates
                                                titleId="offering.customOffering.action.title"
                                                formState={values.offeringMeta.customDates}
                                                dateContext="offeringMeta.customDates"
                                                setFieldValue={setFieldValue}
                                                errors={errors.offeringMeta?.customDates}
                                            // addHandler={handleCustomDateAdd}
                                            // deleteHandler={handleCustomDateDelete}
                                            // arrayHelpers={arrayHelpers}
                                            />
                                        ) : (
                                            <FullDates
                                                titleId="offering.excluded"
                                                formState={values.offeringMeta.excludedDates}
                                                dateContext="offeringMeta.excludedDates"
                                                errors={errors.offeringMeta?.excludedDates}
                                                setFieldValue={setFieldValue}
                                            />
                                        )}
                                    </Box>
                                    {/* <FormikTextField
                                    name="description"
                                    label={<IntlMessages id='offering.description' />}
                                    variant='outlined'
                                    fullWidth
                                    multiline
                                    rowsMax={10}
                                /> */}



                                </Box>
                            </Grid>
                            <Grid item xs={12} md={isEdit ? 12 : 6}>
                                <Box className={classes.formRightPane}>
                                    <FieldArray
                                        name={'offeringTimes.times'}
                                        render={arrayHelpers => (
                                            <OfferingTimes
                                                formContext="offeringTimes"
                                                formState={values.offeringTimes}
                                                setFieldValue={setFieldValue}
                                                errors={errors.offeringTimes}
                                                arrayHelpers={arrayHelpers}
                                                offering={offering}
                                            />
                                        )}
                                    />
                                </Box>
                            </Grid>
                            {values.offeringTimes.times.length === 0 && (
                                <Grid item xs={12} md={isEdit ? 12 : 6}>
                                    <PoolSelect pools={values.poolConnections} setPools={(pools) => { setFieldValue('poolConnections', pools) }} />
                                </Grid>
                            )}
                            <Grid item xs={12}>
                                {temple?.closingTime && (
                                    <FormControl margin="normal" component="fieldset">
                                        <FormLabel component="legend">{messages['offering.dateRangeCalc']}</FormLabel>
                                        <RadioGroup
                                            row
                                            aria-label="use closing time"
                                            name={'useClosingTime'}
                                            value={values.useClosingTime === undefined || values.useClosingTime === true ? 'use_closing_time' : 'use_24_hr'}
                                            onChange={event => setFieldValue(event.target.name, event.target.value === 'use_closing_time' ? true : false)}
                                        >
                                            <FormControlLabel
                                                value="use_closing_time"
                                                control={<Radio />}
                                                label={messages['offering.useClosingTime']}
                                            />
                                            <FormControlLabel
                                                value="use_24_hr"
                                                control={<Radio />}
                                                label={messages['offering.use24HrClock']}
                                            />
                                        </RadioGroup>
                                    </FormControl>
                                )}
                            </Grid>
                            {<Grid item xs={12} >
                                <Box component="h1" fontSize="1.3rem" ml={2}>
                                    Online Booking
                                </Box>
                                <Divider style={{ marginTop: "1rem", marginBottom: "2rem" }} variant="middle" />
                                <FormikTextField
                                    select
                                    placeholder={messages['offering.blockPeriod']}
                                    name='blockPeriod'
                                    label={<IntlMessages id='offering.blockPeriod' />}
                                    variant='outlined'
                                    fullWidth
                                >
                                    {Object.entries(BlockPeriods).filter(([keys, value]) => (value === BlockPeriods.DAY || value === BlockPeriods.NA)).map(([key, value]) => (
                                        <MenuItem key={key} value={value}>
                                            {capStr(value).split('_').join(' ')}
                                        </MenuItem>
                                    ))}
                                </FormikTextField>
                                {values.blockPeriod && values.blockPeriod !== BlockPeriods.NA && (
                                    <FormikTextField
                                        placeholder={messages['offering.blockUnit']}
                                        name='blockUnit'
                                        label={<IntlMessages id='offering.blockUnit' />}
                                        variant='outlined'
                                        type='number'
                                        fullWidth
                                    />)}
                                {values.offeringType !== OfferingTypes.CUSTOM && (
                                    <FormikTextField
                                        placeholder={messages['offering.repeatUnit']}
                                        name='repeatUnit'
                                        label={<IntlMessages id='offering.repeatUnit' />}
                                        variant='outlined'
                                        type='number'
                                        fullWidth
                                    />
                                )}
                                <Divider style={{ marginTop: "2.5rem", marginBottom: "2rem" }} variant="middle" />
                                <FormikSwitchField
                                    label='Allow Multiple Devotees'
                                    name="allowMultipleDevotees"
                                />
                                <FormikSwitchField
                                    label='Is Dakshina Required'
                                    name="isDakshinaRequired"
                                />
                                {values.isDakshinaRequired && <FormikTextField
                                    placeholder={messages['offering.minDakshinaAmount']}
                                    name='minDakshinaAmount'
                                    label={<IntlMessages id='offering.minDakshinaAmount' />}
                                    variant='outlined'
                                    type='number'
                                    fullWidth
                                />}
                            </Grid>}
                            {/* <Grid item container xs={12} md={isEdit ? 12 : 6}>
                        <Box className={classes.formRightPane}>
                            <FormBlock titleId="offerings.deity.assign.title">
                                <DeityTransferList
                                    chosenDeityLinks={values.assignedDeities}
                                    handleAddDeities={(val) => { setFieldValue('assignedDeities',[...values.assignedDeities,...val.map(e => e.url)])}}
                                    handleRemoveDeities={(val) => { setFieldValue('assignedDeities',values.assignedDeities.filter((ad => val.some(e => e.url !== ad))))}}
                                />
                            </FormBlock>
                            </Box>
                        </Grid> */}
                            <Grid item xs={12}>
                                <Button disabled={isSubmitting || !isValid} variant="contained" color="primary" type="submit">
                                    <IntlMessages id={buttonLabelId} />
                                </Button>
                            </Grid>
                            <Grid item container spacing={4} direction="column" alignItems="flex-start">
                                {alerts.map((alert, index) => (
                                    <Grid item key={index}>
                                        <Alert severity={alert.severity}>{alert.message}</Alert>
                                    </Grid>
                                ))}
                                {api.status === apiStatus.FAILED && !isFieldError(api.error) && (
                                    <Grid item>
                                        <Alert severity="error">{extractError(api.error)}</Alert>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Form>
                )
            }}
        </Formik>
    )
}

export default React.memo(FormOffering)
